"use client";

import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopNavigation() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex items-center gap-4">
        <div className="h-4 w-16 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-4 w-24 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-4 py-2 text-xs font-mono font-medium uppercase tracking-widest text-foreground transition-colors hover:text-primary">
                {item.label}
                <span className="absolute inset-x-4 bottom-1 h-[1px] origin-left scale-x-0 bg-primary transition-transform duration-300 ease-out group-hover:scale-x-100 shadow-[0_0_8px_var(--primary)]" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNavigation({
  isOpen,
  setIsOpen
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-border shadow-2xl md:hidden flex flex-col px-4 py-6 gap-4 z-40">
      {loading ? <div className="flex flex-col gap-4">
          <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
          <div className="h-6 w-40 bg-muted animate-pulse rounded-md" />
          <div className="h-6 w-24 bg-muted animate-pulse rounded-md" />
        </div> : <nav className="flex flex-col gap-2">
          {menuItems.map((item, i) => <div key={item.id} data-index={i}>
              <Link to={item.href || "#"} className="block py-3 text-sm font-mono uppercase tracking-wider text-foreground border-b border-border/20 last:border-0 hover:text-primary transition-colors" onClick={() => setIsOpen(false)}>
                {item.label}
              </Link>
            </div>)}
        </nav>}
      <div className="pt-4 flex flex-col gap-3">
        <Button asChild size="lg" className="w-full font-default font-bold bg-destructive hover:bg-destructive/90 text-destructive-foreground animate-pulse rounded-sm">
          <Link to="/ContactPage" onClick={() => setIsOpen(false)}>
            Notfall-Kontakt
          </Link>
        </Button>
      </div>
    </div>;
}
export default function MainHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };
  return <section data-section-id="223390" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 ${isScrolled ? "bg-background/80 backdrop-blur-xl border-b border-border/50 shadow-lg" : "bg-background border-b border-transparent"}`}>
      <MenuProvider menu_id="41" className="w-full">
        <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between relative">
          
          {}
          <div className="flex items-center flex-shrink-0">
            <Link to="/" className="block transition-opacity hover:opacity-80">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1652372/img/securenetics_logo_white.png" alt="SecureNetics" className="h-8 w-auto object-contain" />
            </Link>
          </div>

          {}
          <div className="flex-1 flex justify-center">
            <DesktopNavigation />
          </div>

          {}
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="hidden md:block">
              <Button asChild variant="destructive" className="font-default font-bold rounded-sm shadow-[0_0_15px_rgba(239,68,68,0.4)] hover:shadow-[0_0_25px_rgba(239,68,68,0.6)] transition-all duration-300">
                <Link to="/ContactPage">Notfall-Kontakt</Link>
              </Button>
            </div>

            {}
            <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:bg-primary/10" onClick={toggleMobileMenu} aria-label="Toggle navigation menu" aria-expanded={isMobileMenuOpen}>
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>

          {}
          <MobileNavigation isOpen={isMobileMenuOpen} setIsOpen={setIsMobileMenuOpen} />
          
        </div>
      </MenuProvider>
    </section>;
}